import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    ViewEncapsulation,
    signal,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25LoadingApi } from "../s25-loading/loading.api";
import { LangService } from "../../services/lang.service";
import { DataAccess } from "../../dataaccess/data.access";
import { EventService } from "../../services/event.service";

@TypeManagerDecorator("s25-ng-subscribe")
@Component({
    selector: "s25-ng-subscribe",
    template: `
        <div>
            @if (init) {
                @if (type === "more-actions") {
                    <div class="moreActionsSection">
                        <s25-loading-inline [model]="{}"></s25-loading-inline>
                        <div class="feed-container">
                            @if (atomUrl) {
                                <a [href]="atomUrl" target="_blank" rel="noopener" class="ngAtomText">
                                    <div class="ngAtom ngMinImgDim"></div>
                                    {{ viewAtomFeed }}
                                </a>
                            }
                            <a [href]="iCalUrl" target="_blank" class="ngICalText">
                                <div class="ngICal ngMinImgDim"></div>
                                {{ viewICalFeed }}
                            </a>
                        </div>

                        <div class="modal-footer">
                            <div class="ngFinePrint ngCenterAlignText">
                                <p [innerHTML]="footerText"></p>
                            </div>
                        </div>
                    </div>
                }

                @if (type === "embedded") {
                    <div class="embeddedSubscribe c-margin-top--half">
                        <a (click)="toggleEmbedded()" [class.active]="embeddedOpen()" class="embeddedSubscribeButton">
                            <s25-ng-icon [type]="'calendar'" [ariaLabel]="false" />
                            Add To Calendar
                        </a>

                        <div [class.active]="embeddedOpen()" class="embeddedSubscribeDivider"></div>

                        <s25-loading-inline [model]="{}"></s25-loading-inline>

                        <div
                            [class.embeddedSubscribe--hide]="!embeddedOpen()"
                            class="c-margin-left--single embeddedSubscribeContent"
                        >
                            <div>
                                <button
                                    class="c-button c-button--flat"
                                    (click)="closeEmbeddedSubscribe()"
                                    aria-label="Close"
                                    role="button"
                                    tabindex="0"
                                >
                                    <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                                </button>

                                <div class="s25subscription-feeds ngInlineBlock">
                                    <a
                                        [href]="iCalUrl"
                                        target="_blank"
                                        class="ngICalText"
                                        (click)="closeEmbeddedSubscribe()"
                                    >
                                        <div
                                            class="ngICal ngMinImgDim ngInlineBlock c-margin-right--half c-verticalAlign--middle"
                                        ></div>
                                        Add to Calendar
                                    </a>
                                </div>

                                @if (atomUrl) {
                                    <div class="s25subscription-feeds ngInlineBlock c-margin-left--double">
                                        <a
                                            [href]="atomUrl"
                                            target="_blank"
                                            rel="noopener"
                                            class="ngAtomText"
                                            (click)="closeEmbeddedSubscribe()"
                                        >
                                            <div
                                                class="ngAtom ngMinImgDim ngInlineBlock c-margin-right--half c-verticalAlign--middle"
                                            ></div>
                                            View Atom Feed
                                        </a>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                }
            }
        </div>
    `,
    styles: `
        .moreActionsSection {
            padding-block: 0 1em !important;

            .feed-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 8px;
                padding: 0.5em;

                .ngAtomText,
                .ngICalText {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25SubscribeComponent implements OnInit {
    @Input() itemId: number;
    @Input() itemTypeId: number;
    @Input() type?: string;

    embeddedOpen = signal(false);
    title = "";
    protocol = "";
    host = "";
    init: boolean = false;
    iconClass?: string;
    atomUrl?: string;
    iCalUrl?: string;
    webCalUrl?: string;
    viewICalFeed?: string;
    viewAtomFeed?: string;
    footerText?: string;

    private readonly typeMap: Record<number, any> = {
        1: {
            service: EventService.getEventMinimal.bind(EventService),
            serviceUrl: "/event.xml?event_id=",
            iCalUrl: "/reservations.ics?event_id=",
            nameProp: "event_name",
        },
        4: {
            service: () => Promise.resolve(),
            serviceUrl: "/space.xml?space_id=",
            iCalUrl: "/rm_reservations.ics?space_id=",
        },
        6: {
            service: () => Promise.resolve(),
            serviceUrl: "/resource.xml?resource_id=",
            iCalUrl: "/rs_reservations.ics?resource_id=",
        },
        10: {
            service: () => Promise.resolve(),
            serviceUrl: "",
            iCalUrl: "/reservations.ics?tasks=T&end_dt=+7&options=standard+task_agenda",
        },
    };

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {
        this.elementRef.nativeElement.angBridge = this;
    }

    ngOnInit(): void {
        this.iconClass = "ngSubscribe";

        this.protocol = window.location.protocol + "//";
        this.host = window.location.hostname;

        this.init = true;
        this.cd.detectChanges();
    }

    async getSubscribe(): Promise<void> {
        S25LoadingApi.init(this.elementRef.nativeElement);
        this.cd.detectChanges();
        const service = this.typeMap[this.itemTypeId].service;

        try {
            const itemData = await service(this.itemId, true);
            const lang = await LangService.getLang();

            this.title = lang.div.controls.application.text.subscriptions.subscription_options;

            const baseUrl = this.typeMap[this.itemTypeId].serviceUrl;
            const iCalUrl = this.typeMap[this.itemTypeId].iCalUrl;

            if (baseUrl) {
                this.atomUrl = DataAccess.getUrl(
                    `${baseUrl}${this.itemId}&live_url=${this.protocol}${this.host}${location.pathname}&otransform=publish/25live_atom.xsl`,
                );
            }

            this.iCalUrl = DataAccess.getUrl(iCalUrl);

            if (this.itemTypeId === 1 && itemData.start_date && itemData.end_date) {
                this.iCalUrl += `${this.itemId}&start_dt=${itemData.start_date.replace(/-/g, "")}&end_dt=${itemData.end_date.replace(/-/g, "")}&options=standard`;
            } else if (this.itemTypeId === 4 || this.itemTypeId === 6) {
                this.iCalUrl += `${this.itemId}&start_dt=-30&end_dt=+180&options=standard`;
            }

            this.webCalUrl = this.iCalUrl.replace(/^http:/, "webcal:");
            this.viewICalFeed = lang.div.controls.application.text.subscriptions.view_ical_feed;
            this.viewAtomFeed = lang.div.controls.application.text.subscriptions.view_atom_feed;
            this.footerText = `
        <span>To import the iCal into Microsoft Outlook, click to download, then "Upload from file".
        To stay subscribed to changes on the events, right-click to copy the feed URL and add a new calendar via "Subscribe from web".
        See this Microsoft Outlook <a href='http://windows.microsoft.com/en-us/windows/outlook/calendar-import-vs-subscribe'
        target='_blank' ref='noopener'>support</a> page for more details (and note that "Turn on shared calendar improvements" in advanced Microsoft Account settings must first be disabled).</span>

         </span>
      `;

            this.init = true;
            this.cd.detectChanges();
        } finally {
            S25LoadingApi.destroy(this.elementRef.nativeElement);
            this.cd.detectChanges();
        }
    }

    toggleEmbedded(): void {
        this.embeddedOpen.set(!this.embeddedOpen());
        if (this.embeddedOpen()) {
            this.getSubscribe();
        }
    }

    closeEmbeddedSubscribe(): void {
        this.embeddedOpen.set(false);
    }
}
